import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { ModuleParams, ModuleResponse } from './types';

/** Build the /api/v1/modules query params from a ModuleParams — shared by the
 *  list hook and the "export all rows" fetch so both stay in lock-step. */
function moduleQueryParams(p: ModuleParams): Record<string, string | number> {
  const params: Record<string, string | number> = { page: p.page, pageSize: p.pageSize };
  if (p.search) params.search = p.search;
  if (p.rangeDays != null) params.rangeDays = p.rangeDays;
  if (p.fromDate) params.fromDate = p.fromDate;
  if (p.toDate) params.toDate = p.toDate;
  if (p.analysisFrom) params.analysisFrom = p.analysisFrom;
  if (p.analysisTo) params.analysisTo = p.analysisTo;
  if (p.collectedFrom) params.collectedFrom = p.collectedFrom;
  if (p.collectedTo) params.collectedTo = p.collectedTo;
  if (p.conditions && p.conditions.length) params.query = JSON.stringify(p.conditions);
  if (p.sort) { params.sort = p.sort; params.dir = p.sortDesc ? 'desc' : 'asc'; }
  const active = Object.fromEntries(Object.entries(p.filters).filter(([, v]) => v && v.length > 0));
  if (Object.keys(active).length) params.filters = JSON.stringify(active);
  return params;
}

/** One-shot fetch of a module list (used by the hook AND by "export all rows",
 *  which passes a huge pageSize to pull every filtered row for the export). */
export async function fetchModuleRows(key: string, p: ModuleParams): Promise<ModuleResponse> {
  const { data } = await api.get<ModuleResponse>(`/api/v1/modules/${key}`, { params: moduleQueryParams(p) });
  return data;
}

export function useModuleList(key: string, p: ModuleParams) {
  return useQuery({
    queryKey: ['module', key, p],
    queryFn: () => fetchModuleRows(key, p),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Distinct values of one column, for the filter dropdown (lazy). */
export function useColumnDistinct(
  key: string, column: string, q: string, rangeDays: number | null, enabled: boolean,
  filters?: Record<string, string[]>, desc = false,
) {
  // Only the OTHER columns' active selections cascade this dropdown.
  const active = filters
    ? Object.fromEntries(Object.entries(filters).filter(([k, v]) => k !== column && v && v.length > 0))
    : {};
  const filtersJson = Object.keys(active).length ? JSON.stringify(active) : undefined;
  return useQuery({
    queryKey: ['module-distinct', key, column, q, rangeDays, filtersJson, desc],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; values: string[] }>(
        `/api/v1/modules/${key}/distinct/${column}`,
        { params: { q: q || undefined, rangeDays: rangeDays ?? undefined, filters: filtersJson,
          desc: desc || undefined } },
      );
      return data.values ?? [];
    },
    // The distinct-value query is a heavy server round-trip (full module CTE),
    // so cache aggressively: once a column's list is loaded, re-opening the
    // filter is instant for 10 min instead of re-fetching + showing "Loading…".
    staleTime: 600_000,
    gcTime: 900_000,
    placeholderData: (prev) => prev,
  });
}
